/**
 * Extractos y metadatos derivados.
 *
 * El admin guarda el contenido en un solo campo. Estos helpers derivan de ahi
 * el extracto visible en los listados y los textos que consumen Open Graph y
 * Twitter, sin obligar a duplicar a mano lo que ya esta escrito.
 */

import { docToPlainText, docFromPlainText } from "./document";
import { collapseWhitespace, stripBrandSuffix } from "./text";
import type { RichDoc, RichNode } from "./types";

export const EXCERPT_MAX = 200;
export const META_DESCRIPTION_MAX = 160;

/**
 * Recorta en la ultima palabra completa y anade elipsis si hizo falta.
 * No corta a mitad de palabra, que es lo que produce snippets rotos en Google.
 */
export function truncate(value: string, max: number): string {
  const normalized = collapseWhitespace(value);
  if (normalized.length <= max) return normalized;

  const cut = normalized.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  const safe = lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut;
  return `${safe.replace(/[,;:.!?-]+$/, "")}…`;
}

/**
 * Extracto para los listados: usa el primer parrafo con contenido real,
 * saltando el H1 si alguien lo dejo pegado en el cuerpo.
 */
export function buildExcerpt(doc: RichDoc, max = EXCERPT_MAX): string {
  for (const node of doc.content) {
    if (node.type === "heading") continue;
    if (node.type === "faqList" || node.type === "table" || node.type === "image") continue;

    const text = collapseWhitespace(collectText(node));
    if (text.length > 0) return truncate(text, max);
  }
  const fallback = collapseWhitespace(docToPlainText(doc));
  return fallback ? truncate(fallback, max) : "";
}

function collectText(node: RichNode): string {
  if (node.type === "text") return node.text ?? "";
  const parts: string[] = [];
  const walk = (current: RichNode) => {
    if (current.type === "text" && current.text) parts.push(current.text);
    current.content?.forEach(walk);
  };
  node.content?.forEach(walk);
  return parts.join(" ");
}

export interface MetaCopy {
  title: string;
  description: string;
}

/**
 * Resuelve el titulo y la descripcion finales respetando la marca del sitio.
 *
 * `Layout.astro` agrega " · Vimos Digital" al title, asi que este helper
 * devuelve la parte del autor y evita que la marca quede duplicada cuando el
 * titulo fue pegado desde otro lugar con la firma incluida.
 */
export function buildMetaCopy(input: {
  title: string;
  seoTitle?: string | null;
  seoDescription?: string | null;
  excerpt?: string | null;
  doc?: RichDoc | null;
}): MetaCopy {
  const title = stripBrandSuffix(input.seoTitle ?? input.title);

  const descriptionSource = input.seoDescription?.trim()
    ? input.seoDescription
    : input.excerpt?.trim() || (input.doc ? buildExcerpt(input.doc, META_DESCRIPTION_MAX) : "");

  return {
    title: title || "Vimos Digital",
    description: truncate(collapseWhitespace(descriptionSource), META_DESCRIPTION_MAX),
  };
}

/** Convierte texto plano en doc. Reexportado por comodidad del admin. */
export { docFromPlainText };
